import React, { useState, useEffect, useRef } from 'react';
import { Typography, Spin } from 'antd';
import { HotTable } from '@handsontable/react';
import Handsontable from 'handsontable';
import { getColumns, getCellRenderer } from '../utils/tableUtils';
import { isColorLight } from '../utils/colorUtils';
import { getCurrencySymbol, formatMetricValue } from '../utils/formatters';
import api from '../../../services/api';
import dayjs from 'dayjs';

const { Text } = Typography;

// Kampanya renklerini belirleme - her kampanya için farklı bir renk seçilecek
const campaignColors = [
  '#FF5722', // Turuncu
  '#2196F3', // Mavi
  '#4CAF50', // Yeşil
  '#9C27B0', // Mor
  '#E91E63', // Pembe
  '#00BCD4', // Açık Mavi
  '#8BC34A', // Açık Yeşil
  '#FFC107', // Sarı
  '#795548', // Kahverengi
  '#607D8B'  // Gri Mavi
];

// Kampanya adına göre renk döndüren fonksiyon
const getCampaignColor = (campaignName) => {
  if (!campaignName) return '#000000';
  
  // Kampanya adının toplam karakter değerine göre renk seçimi
  let sum = 0;
  for (let i = 0; i < campaignName.length; i++) {
    sum += campaignName.charCodeAt(i);
  }
  
  // Mod işlemi ile renk dizisinden seçim yapılıyor
  return campaignColors[sum % campaignColors.length];
};

/**
 * Günlük metrikler tablosu bileşeni
 */
const DailyMetricsTable = ({
  brandId,
  gridData,
  metrics,
  allMetricsData,
  loading
}) => {
  const hotTableRef = useRef(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  // Aktif kampanyaların renklerini tutacak state
  const [campaignClassMap, setCampaignClassMap] = useState({});
  // Seçilen hücrelerin toplamını tutacak state
  const [selectionSummary, setSelectionSummary] = useState({ count: 0, sum: 0, average: 0 });
  // Tooltip state'leri
  const [tooltip, setTooltip] = useState({ show: false, content: '', x: 0, y: 0 });

  // Tooltip gösterme fonksiyonu
  const showTooltip = (event, content) => {
    const rect = event.target.getBoundingClientRect();
    setTooltip({
      show: true,
      content: content,
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
  };

  // Tooltip gizleme fonksiyonu
  const hideTooltip = () => {
    setTooltip({ show: false, content: '', x: 0, y: 0 });
  };

  // Kampanyaları yükle
  useEffect(() => {
    const fetchCampaigns = async () => {
      if (!brandId) return;

      try {
        setLoadingCampaigns(true);
        const response = await api.get('/campaigns');
        // Sadece bu markaya ait kampanyaları filtrele
        const brandCampaigns = response.data.filter(campaign => campaign.brandId === brandId || campaign.Brand?.id === brandId);
        setCampaigns(brandCampaigns);
        
        // Kampanya adlarına göre CSS sınıf isimleri oluştur
        const classMap = {};
        brandCampaigns.forEach(campaign => {
          const className = `campaign-comment-${campaign.id}`;
          classMap[campaign.id] = {
            className,
            color: getCampaignColor(campaign.name)
          };
        });
        setCampaignClassMap(classMap);
      } catch (error) {
        console.error('Kampanyalar yüklenirken bir hata oluştu:', error);
      } finally {
        setLoadingCampaigns(false);
      }
    };

    fetchCampaigns();
  }, [brandId]);

  // Tarih için aktif kampanyayı bul
  const findActiveCampaign = (date) => {
    if (!date || !campaigns || campaigns.length === 0) return null;

    // Tarih formatı DD.MM.YYYY olduğu için önce parse et
    const parts = date.split('.');
    const parsedDate = dayjs(`${parts[2]}-${parts[1]}-${parts[0]}`);

    // Kampanyalar içinden o tarihe denk gelenleri bul
    const activeCampaigns = campaigns.filter(campaign => {
      const startDate = dayjs(campaign.startDate);
      const endDate = dayjs(campaign.endDate);
      return parsedDate.isAfter(startDate) && parsedDate.isBefore(endDate) || 
             parsedDate.isSame(startDate) || 
             parsedDate.isSame(endDate);
    });

    return activeCampaigns.length > 0 ? activeCampaigns : null;
  };

  if (loading || loadingCampaigns) {
    return (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <Spin />
        <p>Veriler yükleniyor...</p>
      </div>
    );
  }

  if (!gridData[brandId] || gridData[brandId].length === 0) {
    return <Text type="secondary">Bu marka için günlük metrik verisi bulunamadı.</Text>;
  }

  // Marka para birimini al
  const brandCurrency = allMetricsData[brandId]?.brandInfo?.currency || 'TRY';
  const columns = getColumns(brandId, metrics);

  return (
    <div style={{ width: '100%', height: 'auto', overflow: 'visible' }}>
      <HotTable
        ref={hotTableRef}
        data={gridData[brandId]}
        colHeaders={columns.map(col => col.title)}
        columns={columns}
        rowHeaders={function(index) {
          const data = gridData[brandId];
          if (!data || !data[index]) return index + 1;
          
          // Aylık satır için özel başlık
          if (index === 0 && data[index].isMonthlyRow) {
            return 'TOTAL';
          }
          
          // Diğer satırlar için tarih başlığı
          return data[index].dateObj ? data[index].dateObj.format('D MMM') : (index + 1);
        }}
        width="100%"
        height="auto"
        autoRowSize={true}
        autoColumnSize={true}
        stretchH="all"
        licenseKey="non-commercial-and-evaluation"
        readOnly={true}
        className="htCenter"
        fixedRowsTop={1}
        // HTML başlıkları etkinleştir
        allowHtml={true}
        // Comments'i etkinleştir
        comments={true}
        // Satır yüksekliğini ayarla - daha kısa boyutlar
        rowHeights={function(index) {
          return Math.max(22, index === 0 ? 28 : 22); // İlk satır (aylık) biraz daha yüksek, diğerleri küçültüldü
        }}
        afterGetColHeader={(col, TH) => {
          if (col >= 0) {
            const sortedMetrics = [...metrics[brandId]].sort((a, b) => {
              const orderA = a.BrandMetric?.displayOrder || 0;
              const orderB = b.BrandMetric?.displayOrder || 0;
              return orderA - orderB;
            });
            
            if (sortedMetrics[col]) {
              const metric = sortedMetrics[col];
              const spanElement = TH.querySelector('.colHeader');
              if (spanElement) {
                const originalText = metric.description || metric.name;
                const words = originalText.split(' ');
                
                if (words.length > 2) {
                  const firstPart = words.slice(0, 2).join(' ');
                  const secondPart = words.slice(2).join(' ');
                  
                  // Başlığı iki satıra böl ve dikey ortala
                  spanElement.innerHTML = `
                    <div style="line-height: 1.2; white-space: normal; display: flex; flex-direction: column; justify-content: center; height: 100%;">
                      <div style="text-align: center;">${firstPart}</div>
                      <div style="text-align: center;">${secondPart}</div>
                    </div>
                  `;
                }
                
                // Başlık hücresinin yüksekliğini artır ve ortala
                TH.style.height = 'auto';
                TH.style.minHeight = '50px';
                TH.style.whiteSpace = 'normal';
                TH.style.verticalAlign = 'middle';
                
                // Tüm hücreyi dikey olarak ortala
                const containerDiv = TH.querySelector('.relative');
                if (containerDiv) {
                  containerDiv.style.height = '100%';
                  containerDiv.style.display = 'flex';
                  containerDiv.style.alignItems = 'center';
                  containerDiv.style.justifyContent = 'center';
                }
                
                // Metrik rengi varsa başlığa uygula
                if (metric.color) {
                  TH.style.backgroundColor = metric.color;
                  TH.style.color = isColorLight(metric.color) ? '#000000' : '#FFFFFF';
                }
                
                // Tooltip için dahili notları, description veya metrik adını kullan
                let tooltipText = '';
                if (metric.internalNotes && metric.internalNotes.trim()) {
                  tooltipText = metric.internalNotes.trim();
                } else if (metric.description && metric.description.trim() && metric.description !== metric.name) {
                  tooltipText = metric.description.trim();
                } else {
                  tooltipText = metric.name;
                }
                
                // Mouse event listener'ları ekle
                TH.style.cursor = 'help';
                
                // Mevcut event listener'ları temizle
                TH.removeEventListener('mouseenter', TH._tooltipMouseEnter);
                TH.removeEventListener('mouseleave', TH._tooltipMouseLeave);
                
                // Yeni event listener'ları ekle
                TH._tooltipMouseEnter = (event) => showTooltip(event, tooltipText);
                TH._tooltipMouseLeave = () => hideTooltip();
                
                TH.addEventListener('mouseenter', TH._tooltipMouseEnter);
                TH.addEventListener('mouseleave', TH._tooltipMouseLeave);
              }
            }
          }
        }}
        cells={(row, col, prop) => {
          const data = gridData[brandId];
          if (!data) return {};

          const cellProperties = {}; // Hücre özelliklerini tutacak nesne
          
          // Metrik bilgilerini al (doğru sütun index'i ile)
          const sortedMetrics = [...metrics[brandId]]
            .filter(metric => metric.BrandMetric?.isVisible !== false)
            .sort((a, b) => {
              const orderA = a.BrandMetric?.displayOrder || 0;
              const orderB = b.BrandMetric?.displayOrder || 0;
              return orderA - orderB;
            });
            
          const metric = col >= 0 ? sortedMetrics[col] : null;

          // Sadece ilk sütundaki hücrelere kampanya bilgilerini ekle (col === 0 ve aylık satır değilse)
          if (col === 0 && row > 0) {
            const rowData = data[row];
            if (rowData && rowData.date) {
              const activeCampaigns = findActiveCampaign(rowData.date);
              
              if (activeCampaigns && activeCampaigns.length > 0) {
                // Kampanya adlarını içeren yorum mesajı oluştur
                const campaignNames = activeCampaigns.map(c => c.name).join(', ');
                
                // Kampanya rengini belirle - ilk kampanyayı kullan
                const campaignId = activeCampaigns[0].id;
                const campaignInfo = campaignClassMap[campaignId];
                
                // CSS sınıfını ve kampanya comment'ini ekle
                if (campaignInfo) {
                  cellProperties.className = campaignInfo.className;
                  cellProperties.comment = { 
                    value: `${campaignNames}`,
                    style: { width: 215, height: 70 }
                  };
                }
              }
              // Kampanya yoksa comment ekleme (değiştirildi)
            }
          }

          // Aylık satır için özel stil
          if (row === 0 && data[row]?.isMonthlyRow) {
            cellProperties.className = (cellProperties.className || '') + ' monthly-row';
            if (metric) {
              cellProperties.renderer = getCellRenderer(metric, brandId, allMetricsData);
            }
          }
          // Günlük satırlar için
          else if (metric) {
            // Varsayılan renderer
            cellProperties.renderer = getCellRenderer(metric, brandId, allMetricsData);
            
            // Sadece ilk sütun dışındaki otomatik hesaplanan metrikler için stil sınıfı ekle
            if (col !== 0 && metric.isAutoCalculated) {
              cellProperties.className = (cellProperties.className || '') + ' auto-calculated-cell';
            }
          }

          // Okuma modu her zaman açık
          cellProperties.readOnly = true;

          return cellProperties;
        }}
        afterSelection={(startRow, startCol, endRow, endCol) => {
          // Seçilen hücrelerin değerlerini topla
          let sum = 0;
          let count = 0;
          
          // Satır ve sütun sınırlarını belirle (başlangıç küçükse yer değiştir)
          const rowStart = Math.min(startRow, endRow);
          const rowEnd = Math.max(startRow, endRow);
          const colStart = Math.min(startCol, endCol);
          const colEnd = Math.max(startCol, endCol);
          
          // Metrik tipini belirle - ilk seçilen sütunun metrik tipini kullanacağız
          const sortedMetrics = [...metrics[brandId]]
            .filter(metric => metric.BrandMetric?.isVisible !== false)
            .sort((a, b) => {
              const orderA = a.BrandMetric?.displayOrder || 0;
              const orderB = b.BrandMetric?.displayOrder || 0;
              return orderA - orderB;
            });
          
          // Seçilen ilk sütun için metrik bilgisini al (tüm sütunlar aynı tipte olmayabilir)
          const selectedMetric = sortedMetrics[colStart] || { dataType: 'number' };
          const metricDataType = selectedMetric.dataType || 'number';
          
          // Seçilen hücrelerde döngü
          for (let row = rowStart; row <= rowEnd; row++) {
            for (let col = colStart; col <= colEnd; col++) {
              // Artık tüm sütunlardan toplam alıyoruz
              // Hücre değerini al
              const value = hotTableRef.current?.hotInstance?.getDataAtCell(row, col);
              
              // Eğer değer sayısal ise toplama ekle
              if (value !== null && value !== undefined && !isNaN(parseFloat(value))) {
                sum += parseFloat(value);
                count++;
              }
            }
          }
          
          // Ortalama hesapla
          const average = count > 0 ? sum / count : 0;
          
          // Formatlanmış değerler
          const formattedSum = formatMetricValue(sum, metricDataType, brandCurrency);
          const formattedAverage = formatMetricValue(average, metricDataType, brandCurrency);
          
          // State'i güncelle
          setSelectionSummary({
            count,
            sum: formattedSum,
            average: formattedAverage,
            rawSum: sum,
            rawAverage: average
          });
        }}
      />
      
      {/* Yorum kutuları ve kampanya renklerinin stilleri */}
      <style jsx global>{`
        .htCommentsContainer .htComments {
          width: 215px !important;
          height: 70px !important;
        }
        .htCommentTextArea {
          width: 215px !important;
          height: 70px !important;
        }
        .auto-calculated-cell {
          background-color: #f5f5f5;
        }
        
        /* Kampanya yorumları için özel stiller */
        ${Object.values(campaignClassMap).map(info => `
          .handsontable .${info.className}.htCommentCell::after {
            border-top: 6px solid ${info.color} !important;
            border-left: 6px solid transparent !important;
          }
        `).join('\n')}
        
        /* Tablo özet çubuğu */
        .table-summary-bar {
          display: flex;
          justify-content: flex-end;
          padding: 8px 16px;
          background-color: #f5f5f5;
          border: 1px solid #e0e0e0;
          border-top: none;
          font-size: 14px;
          margin-top: -1px;
        }
        
        .summary-item {
          margin-left: 20px;
          display: flex;
          align-items: center;
        }
        
        .summary-label {
          font-weight: bold;
          margin-right: 8px;
        }
        
        .summary-value {
          font-family: 'Courier New', monospace;
        }
        
        /* Custom tooltip stilleri */
        .custom-tooltip {
          position: fixed;
          background-color: #333;
          color: white;
          padding: 8px 12px;
          border-radius: 4px;
          font-size: 12px;
          z-index: 10000;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          max-width: 300px;
          word-wrap: break-word;
          text-align: left;
          line-height: 1.4;
          pointer-events: none;
          transform: translateX(-50%);
        }
        
        .custom-tooltip::before {
          content: '';
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          border: 5px solid transparent;
          border-top-color: #333;
        }
      `}</style>
      
      {/* Custom Tooltip */}
      {tooltip.show && (
        <div 
          className="custom-tooltip"
          style={{
            left: tooltip.x,
            top: tooltip.y
          }}
        >
          {tooltip.content}
        </div>
      )}
      
      {/* Tablo özet çubuğu */}
      <div className="table-summary-bar">
        <div className="summary-item">
          <span className="summary-label">Hücre Sayısı:</span>
          <span className="summary-value">{selectionSummary.count}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Toplam:</span>
          <span className="summary-value">{selectionSummary.sum}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Ortalama:</span>
          <span className="summary-value">{selectionSummary.average}</span>
        </div>
      </div>
    </div>
  );
};

export default DailyMetricsTable; 